/**
 * KHIẾU NẠI KPI & CHẤM CÔNG.
 *
 * One endpoint, `POST /staff/kpi/appeals`, backs both forms the spec asks for:
 * its `appealType` discriminates KPI_SCORE / TASK_EVENT / ATTENDANCE /
 * PAYROLL_DEDUCTION and the id fields are all nullable. So "khiếu nại KPI" and
 * "khiếu nại chấm công" are the same form with a different type preselected,
 * rather than two screens that could drift apart.
 *
 * Deep links carry context in router state:
 *   from Lịch sử chấm công → { attendanceEventId }
 *   from Chi tiết KPI      → { appealType: 'KPI_SCORE', kpiScoreId, periodLabel }
 *   from Lịch sử công việc → { appealType: 'TASK_EVENT', taskEventId }
 *
 * `GET /staff/kpi/appeals` returns the employee's own appeals, so the history
 * below is real — including the manager's `decisionNote` and any points
 * adjustment, which is the only place an employee can see that an appeal
 * actually changed something.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage, queryKeys, useAppeals } from '../lib/queries';
import { APPEAL_TYPE_LABEL, approvalStatusLabel, approvalStatusTone } from '../lib/labels';
import type { AppealType } from '../lib/contracts';
import { formatSignedPointsMilli, formatTimeDate } from '../lib/format';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusBadge,
} from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';
import { EvidenceUploader } from '../components/EvidenceUploader';
import { useToast } from '../components/Toast';
import { useSyncState } from '../lib/hooks';

interface AppealRouteState {
  readonly appealType?: AppealType;
  readonly attendanceEventId?: string;
  readonly taskEventId?: string;
  /** From KpiDetailScreen — the exact `kpi_scores` row being disputed. */
  readonly kpiScoreId?: string;
  readonly periodLabel?: string;
}

const schema = z.object({
  appealType: z.enum(['KPI_SCORE', 'TASK_EVENT', 'ATTENDANCE', 'PAYROLL_DEDUCTION']),
  // Mirrors the server's `min(10).max(2000)`.
  reason: z
    .string()
    .min(10, 'Hãy mô tả rõ ít nhất 10 ký tự để quản lý hiểu vấn đề.')
    .max(2000, 'Nội dung quá dài (tối đa 2000 ký tự).'),
});

type FormValues = z.infer<typeof schema>;

const APPEAL_TYPES = Object.keys(APPEAL_TYPE_LABEL) as readonly AppealType[];

export function AppealScreen(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { online } = useSyncState();
  const appeals = useAppeals();

  const state = (location.state as AppealRouteState | null) ?? {};
  const preselected: AppealType =
    state.appealType ?? (state.attendanceEventId !== undefined ? 'ATTENDANCE' : 'KPI_SCORE');

  const [sent, setSent] = useState(false);
  const [evidenceFileId, setEvidenceFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formGeneration, setFormGeneration] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { appealType: preselected, reason: '' },
  });

  const appealType = watch('appealType');

  const submit = useMutation({
    mutationFn: (values: FormValues) =>
      api.createAppeal({
        appealType: values.appealType,
        // `GET /staff/kpi` now returns `kpiScoreId`; KpiDetailScreen passes it
        // through router state so the appeal points at the exact score row.
        kpiScoreId: values.appealType === 'KPI_SCORE' ? (state.kpiScoreId ?? null) : null,
        taskEventId: state.taskEventId ?? null,
        attendanceEventId: state.attendanceEventId ?? null,
        reason: values.reason,
        evidenceFileId,
      }),
    onSuccess: () => {
      setSent(true);
      reset({ appealType, reason: '' });
      // Remount the uploader (via `key`) so the photo cannot silently ride a
      // SECOND appeal the employee files right after.
      setEvidenceFileId(null);
      setFormGeneration((generation) => generation + 1);
      void queryClient.invalidateQueries({ queryKey: queryKeys.appeals });
      toast.success('Đã gửi khiếu nại', 'Quản lý sẽ xem xét và phản hồi.');
    },
    onError: (error) => toast.error('Không gửi được khiếu nại', errorMessage(error)),
  });

  const onSubmit = handleSubmit((values) => submit.mutate(values));

  return (
    <div className="pb-8">
      <ScreenHeader title="Khiếu nại" subtitle="KPI và chấm công" />

      {sent ? <Banner tone="success" title="Khiếu nại của bạn đã được ghi nhận" /> : null}

      {state.attendanceEventId === undefined ? null : (
        <Banner tone="info" title="Khiếu nại cho một lần chấm công cụ thể">
          Mã sự kiện: <span className="font-mono">{state.attendanceEventId}</span>
        </Banner>
      )}
      {state.periodLabel === undefined ? null : (
        <Banner tone="info" title="Khiếu nại điểm KPI">
          Kỳ: <strong>{state.periodLabel}</strong>. Hãy nhắc lại kỳ này trong phần mô tả để quản lý đối chiếu
          đúng.
        </Banner>
      )}

      <form onSubmit={(event) => void onSubmit(event)} className="mt-3 space-y-4">
        <Card>
          <p className="label mb-2">Bạn khiếu nại về việc gì?</p>
          <div className="space-y-2">
            {APPEAL_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setValue('appealType', type, { shouldValidate: true })}
                className={[
                  'min-h-touch-lg w-full rounded-2xl border px-3 text-left font-semibold active:bg-slate-50',
                  type === appealType ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-700',
                ].join(' ')}
              >
                {APPEAL_TYPE_LABEL[type]}
              </button>
            ))}
          </div>
        </Card>

        <Card>
          <label className="label" htmlFor="appeal-reason">
            Mô tả chi tiết
          </label>
          <textarea
            id="appeal-reason"
            rows={6}
            className="field mt-1"
            placeholder="Ví dụ: 14:05 ngày 24/07 tôi chấm công vào ca ở cửa sau, GPS trong nhà báo cách 180 m nhưng tôi có mặt tại cửa hàng. Anh Nam trưởng ca chứng kiến."
            {...register('reason')}
          />
          {errors.reason ? <p className="field-error">{errors.reason.message}</p> : null}
          <p className="mt-2 text-xs text-slate-500">
            Nêu rõ thời gian, địa điểm và người chứng kiến. Khiếu nại có mốc thời gian cụ thể được xử lý nhanh
            hơn nhiều.
          </p>

          <div className="mt-3">
            <EvidenceUploader
              key={formGeneration}
              label="Ảnh bằng chứng (không bắt buộc)"
              hint="Ảnh chụp màn hình, ảnh hiện trường… giúp quản lý xử lý nhanh hơn."
              purpose="EVIDENCE"
              onChange={setEvidenceFileId}
              onBusyChange={setUploading}
            />
          </div>
        </Card>

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={submit.isPending}
          disabled={!online || uploading}
        >
          {uploading ? 'Đang tải ảnh…' : 'Gửi khiếu nại'}
        </Button>
      </form>

      {/* --- history ------------------------------------------------------- */}
      <SectionHeader title="Khiếu nại đã gửi" />
      {appeals.isLoading ? (
        <LoadingState />
      ) : (appeals.data?.appeals.length ?? 0) === 0 ? (
        <EmptyState title="Bạn chưa gửi khiếu nại nào" />
      ) : (
        <ul className="space-y-2">
          {(appeals.data?.appeals ?? []).map((appeal) => (
            <li key={appeal.id}>
              <Card>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900">{APPEAL_TYPE_LABEL[appeal.type] ?? appeal.type}</p>
                    <p className="text-xs text-slate-400">Gửi lúc {formatTimeDate(appeal.createdAt)}</p>
                  </div>
                  <StatusBadge tone={approvalStatusTone(appeal.status)}>
                    {approvalStatusLabel(appeal.status)}
                  </StatusBadge>
                </div>

                <p className="mt-2 text-sm text-slate-700">{appeal.reason}</p>

                {appeal.decisionNote === null ? null : (
                  <p className="mt-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
                    <strong>Phản hồi của quản lý:</strong> {appeal.decisionNote}
                  </p>
                )}

                {appeal.adjustmentPointsMilli === 0 ? null : (
                  <p className="mt-2 text-sm font-semibold text-emerald-700">
                    Điều chỉnh điểm KPI: {formatSignedPointsMilli(appeal.adjustmentPointsMilli)}
                  </p>
                )}

                {appeal.reviewedAt === null ? null : (
                  <p className="mt-1 text-xs text-slate-400">Xử lý lúc {formatTimeDate(appeal.reviewedAt)}</p>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
